import { useRef, useState } from 'react'

import { ChevronRightIcon } from '@radix-ui/react-icons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { GrafbaseThemes } from 'style'

import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import { GithubIcon } from 'components/atomic/atoms/icons/logos/github'
import Text from 'components/atomic/atoms/text'
import { NavbarDropdownProduct } from 'components/pages/@layouts/layout-website/navbar-dropdown-product'
import { NavbarDropdownResources } from 'components/pages/@layouts/layout-website/navbar-dropdown-resources'
import { NavbarDropdownSolutions } from 'components/pages/@layouts/layout-website/navbar-dropdown-solutions'

import { NavbarLinkProps, links } from './links'

const NavbarLink = ({ href, name }: NavbarLinkProps) => {
  const { asPath } = useRouter()
  const current = href === asPath
  const isExternalLink = href.startsWith('https://')
  const ref = useRef<HTMLAnchorElement>(null)

  if (!href) return null

  return (
    <Text as="li" size="sm" css={{ '@sm': { px: '$2' } }}>
      {isExternalLink ? (
        <Text
          as="a"
          href={href}
          target="_blank"
          rel="noopener"
          colorHover="accent"
        >
          {name}
        </Text>
      ) : (
        <Text
          as={Link}
          ref={ref}
          href={href}
          colorHover="accent"
          weight={current ? 'semiBold' : 'regular'}
        >
          {name}
        </Text>
      )}
    </Text>
  )
}

const MobileNavbarDropdown = ({
  name,
  items,
}: {
  name: string
  items: NavbarLinkProps[]
}) => {
  const { asPath } = useRouter()
  const shouldBeOpenAtFirst = items.some(
    ({ href }) => !!href && href === asPath,
  )
  const [isOpen, setIsOpen] = useState(shouldBeOpenAtFirst)

  return (
    <Box as="li" css={{ ml: '-$6' }}>
      <Box
        as="button"
        flex
        items="center"
        css={{ width: '$full', gap: '$2' }}
        onClick={() => setIsOpen(o => !o)}
      >
        <IconStyled
          as={ChevronRightIcon}
          css={{
            rotate: isOpen ? '90deg' : 0,
            transition: 'all .35s',
          }}
        />
        <Text
          size="sm"
          color="text1"
          weight={isOpen ? 'semiBold' : 'regular'}
          transition
        >
          {name}
        </Text>
      </Box>
      <Box
        as="ul"
        flex="column"
        css={{
          gap: '$5',
          ml: '$2',
          pl: '$4',
          borderLeft: '1px solid $bg3',
          opacity: 0,
          maxHeight: 0,
          visibility: 'hidden',
          transition: 'all .35s ease-in-out',
          ...(isOpen && {
            mt: '$2',
            py: '$2',
            opacity: 1,
            maxHeight: '100%',
            visibility: 'visible',
          }),
        }}
      >
        {items.map(props => {
          return <NavbarLink key={props.name} {...props} />
        })}
      </Box>
    </Box>
  )
}

const NavbarLinks = () => {
  return (
    <>
      {/*  DESKTOP */}
      <Box
        as="ul"
        relative // to center dropdown content
        items="center"
        justify="center"
        css={{
          display: 'none',
          spaceX: 3,
          '@sm': {
            display: 'flex',
          },
        }}
      >
        {links
          .filter(f => f.name !== 'Alternatives' && f.name !== 'Use Cases')
          .map(props => {
            if (props.trigger && props.links) {
              return props.name === 'Resources' ? (
                <NavbarDropdownResources
                  key={props.name}
                  name={props.name}
                  items={props.links}
                />
              ) : props.name === 'Product' ? (
                <NavbarDropdownProduct
                  key={props.name}
                  name={props.name}
                  items={props.links}
                />
              ) : (
                <NavbarDropdownSolutions
                  key={props.name}
                  name={props.name}
                  items={props.links}
                />
              )
            }

            return <NavbarLink key={props.name} {...props} />
          })}
        <a
          href="https://github.com/grafbase/grafbase"
          target="_blank"
          rel="noreferrer"
        >
          <Box
            css={{
              background:
                'linear-gradient(98deg, #EAEAEA 0.18%, rgba(244, 244, 244, 0.59) 100.18%)',
              borderRadius: '$md',

              [`.${GrafbaseThemes.dark} &`]: {
                background: 'linear-gradient(30deg, #2E2E32 45%, #9F9F9F 90%)',
                p: '1px',
              },
            }}
          >
            <Box
              flex
              items="center"
              css={{
                px: '10px',
                py: '4px',
                border: '1px solid #C4C4C4',
                borderRadius: '$md',
                spaceX: 2,

                [`.${GrafbaseThemes.dark} &`]: {
                  border: 'none',
                  backgroundColor: '#2E2E32',
                },
              }}
            >
              <IconStyled as={GithubIcon} size="md" />
              <Text size="sm">Star</Text>
            </Box>
          </Box>
        </a>
      </Box>
      {/*  MOBILE */}
      <Box
        css={{
          display: 'block',
          spaceY: 8,
          '@sm': {
            display: 'none',
          },
        }}
      >
        <Box as="ul" flex="column" css={{ gap: '$5', pl: '$6' }}>
          {links.map(props => {
            if (props.trigger && props.links) {
              return (
                <MobileNavbarDropdown
                  key={props.name}
                  name={props.name === 'Product' ? 'Features' : props.name}
                  items={props.links}
                />
              )
            }
            return <NavbarLink key={props.name} {...props} />
          })}
        </Box>
      </Box>
    </>
  )
}

export default NavbarLinks
